import { useCallback } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

export const ANALYTICS_CHANNEL: FabricChannel.growth = FabricChannel.growth;

export const defaultAttributes = {
	packageName: '@atlassian/edition-benefits-modal',
	source: 'editionBenefitsModal',
};

export const getAttributesFromContexts = (context: unknown): Record<string, any> => {
	if (!context || !Array.isArray(context)) {
		return {};
	}

	return context.reduce((acc, x) => {
		if (x.attributes) {
			return {
				...acc,
				...x.attributes,
			};
		}

		return {
			...acc,
			...(x ?? {}),
		};
	}, {});
};

export const useAnalytics = (): {
	sendAnalyticsEvent: (payload: AnalyticsEventPayload | UIAnalyticsEvent) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload | UIAnalyticsEvent): void => {
			if (payload instanceof UIAnalyticsEvent) {
				const contextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						attributes: {
							...contextAttributes,
							...payload.payload.attributes,
							...defaultAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					attributes: {
						...payload.attributes,
						...defaultAttributes,
					},
				});

				const contextAttributes = getAttributesFromContexts(event.context);

				event
					.update({
						attributes: {
							...contextAttributes,
							...event.payload.attributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent],
	);

	return {
		sendAnalyticsEvent,
	};
};

export type OperationalEvent = {
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	attributes: Record<string, any>;
};

/**
 * This hook is designed to be used before contexts are initialized to avoid circular dependencies.
 */
export function useOperationalAnalytics(): (event: OperationalEvent) => void {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		({ action, actionSubject, actionSubjectId, attributes }: OperationalEvent) => {
			const event = createAnalyticsEvent({
				eventType: 'operational',
				action,
				actionSubject,
				actionSubjectId,
				attributes: {
					...defaultAttributes,
					...attributes,
				},
			});

			const contextAttributes = getAttributesFromContexts(event.context);

			event
				.update({
					attributes: {
						...contextAttributes,
						...event.payload.attributes,
					},
				})
				.fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);
}
